import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
    HOURS_7_TO_22,
    planForProjectHour,
    factForProjectHour,
    hourCoverageTone,
} from '../utils';

const COVER_FOLD_KEY = 'pc-day-cover-collapsed';

const HoursHeader = ({ label }) => (
    <div className="pc-day-gr pc-day-hours">
        <div className="pc-day-hname">{label}</div>
        {HOURS_7_TO_22.map((hour) => (
            <div className="pc-day-hcell" key={hour}>
                <span className="pc-day-hfull">{String(hour).padStart(2, '0')}:00</span>
                <span className="pc-day-hshort">{hour}</span>
            </div>
        ))}
    </div>
);

const CoveragePill = ({ fact, plan }) => {
    if (!plan) return <div className="pc-day-cellc" />;
    const tone = hourCoverageTone(fact, plan);
    return (
        <div className="pc-day-cellc">
            <span className={`pc-day-pill pc-day-pill-${tone}`}>{fact}/<b>{plan}</b></span>
        </div>
    );
};

const ResourcesTable = ({
    projects = [],
    schedule = [],
    date,
    highlightedProjectId,
    onProjectClick,
    onClear,
    onSettings,
}) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const [open, setOpen] = useState(() => {
        try {
            return localStorage.getItem(COVER_FOLD_KEY) !== '1';
        } catch (error) {
            return true;
        }
    });

    const toggle = () => {
        setOpen((current) => {
            const next = !current;
            try {
                localStorage.setItem(COVER_FOLD_KEY, next ? '0' : '1');
            } catch (error) {
                /* ignore */
            }
            return next;
        });
    };

    const sortedProjects = useMemo(() => (
        [...projects].sort((a, b) => (a.schedule?.start || 0) - (b.schedule?.start || 0))
    ), [projects]);

    const rows = useMemo(() => sortedProjects.map((project) => ({
        project,
        cells: HOURS_7_TO_22.map((hour) => {
            const plan = planForProjectHour(project, hour);
            const fact = plan ? factForProjectHour(project, hour, schedule, dateStr) : 0;
            return { hour, plan, fact };
        }),
    })), [sortedProjects, schedule, dateStr]);

    return (
        <div className={`pc-day-panel pc-day-panel--cover${open ? '' : ' is-fold'}`}>
            <div className="pc-day-stick">
                <div className="pc-day-ptitle">
                    <button
                        type="button"
                        className="pc-day-fold"
                        aria-expanded={open}
                        aria-label={open ? 'Свернуть покрытие' : 'Развернуть покрытие'}
                        onClick={toggle}
                    >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                            <path d={open ? 'M4 10l4-4 4 4' : 'M4 6l4 4 4-4'} />
                        </svg>
                    </button>
                    <h2>Покрытие по проектам</h2>
                    {open ? (
                        <span className="sub">
                            в клетке: <code>в смене /</code> <b style={{ fontFamily: 'var(--mono)', fontWeight: 500 }}>необходимо</b> за час
                        </span>
                    ) : (
                        <span className="pc-sched-chip">{rows.length}</span>
                    )}
                    <div className="pc-day-rt">
                        {highlightedProjectId
                            ? (
                                <button type="button" className="pc-sched-btn" style={{ height: 22, fontSize: 11 }} onClick={onClear}>
                                    Сбросить
                                </button>
                            )
                            : (open ? <span className="pc-day-hint">клик по проекту подсветит его операторов внизу</span> : null)}
                        {onSettings && (
                            <button type="button" className="pc-sched-btn" style={{ height: 22, fontSize: 11 }} onClick={onSettings}>
                                Настройки
                            </button>
                        )}
                    </div>
                </div>
                {open ? <HoursHeader label="Проект" /> : null}
            </div>
            {open ? rows.map(({ project, cells }) => {
                const selected = highlightedProjectId === project._id;
                const dimmed = highlightedProjectId && !selected;
                return (
                    <div
                        key={project._id}
                        className={`pc-day-gr${dimmed ? ' pc-day-dimrow' : ''}${selected ? ' pc-day-row-sel' : ''}`}
                        onClick={() => onProjectClick && onProjectClick(project._id)}
                    >
                        <div className="pc-day-namec"><span className="nm">{project.name}</span></div>
                        {cells.map((cell) => (
                            <CoveragePill key={cell.hour} fact={cell.fact} plan={cell.plan} />
                        ))}
                    </div>
                );
            }) : null}
        </div>
    );
};

export default ResourcesTable;
